import React, { useState } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { 
  X, 
  Download, 
  FileText,
  FileSpreadsheet,
  FileImage,
  Calendar,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { apiClient, RegulatoryChange } from '../lib/api';

// Import export libraries
// You'll need to install these: npm install jspdf jspdf-autotable xlsx docx file-saver
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableCell, TableRow } from 'docx';
import { saveAs } from 'file-saver';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ExportModal({ isOpen, onClose }: ExportModalProps) {
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [selectedSections, setSelectedSections] = useState({
    summary: true,
    updates: true,
    actionItems: true,
    compliance: true,
    analytics: false
  });
  const [dateRange, setDateRange] = useState('last-7-days');
  const [isExporting, setIsExporting] = useState(false);

  if (!isOpen) return null;

  // Filter regulatory changes by date range
  const filterByDateRange = (changes: RegulatoryChange[]): RegulatoryChange[] => {
    const now = new Date();
    let startDate = new Date();

    switch (dateRange) {
      case 'today':
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'last-7-days':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'last-30-days':
        startDate.setDate(now.getDate() - 30);
        break;
      case 'this-month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'last-month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        break;
      default:
        return changes;
    }

    return changes.filter(change => {
      const changeDate = new Date(change.analysis_timestamp || change.discovered_date);
      return changeDate >= startDate;
    });
  };

  // Export to PDF
  const exportToPDF = async (data: RegulatoryChange[]) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;

    // Title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Infervia Regulatory Changes Report', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // Date
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Summary Section
    if (selectedSections.summary) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Executive Summary', 14, yPos);
      yPos += 8;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total Regulatory Changes: ${data.length}`, 14, yPos);
      yPos += 6;

      const departments = Array.from(new Set(data.flatMap(d => d.affected_departments)));
      doc.text(`Affected Departments: ${departments.join(', ')}`, 14, yPos);
      yPos += 10;
    }

    // Updates Section
    if (selectedSections.updates && data.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Regulatory Changes', 14, yPos);
      yPos += 8;

      const tableData = data.map(change => [
        change.title.substring(0, 50) + '...',
        change.source,
        change.date || 'N/A',
        change.affected_departments.join(', ')
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Title', 'Source', 'Date', 'Departments']],
        body: tableData,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [59, 130, 246] },
        columnStyles: {
          0: { cellWidth: 70 },
          1: { cellWidth: 30 },
          2: { cellWidth: 30 },
          3: { cellWidth: 50 }
        }
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
    }

    // Action Items Section
    if (selectedSections.actionItems) {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Action Items', 14, yPos);
      yPos += 8;

      const actionItems = data.flatMap(change => 
        change.simplified_analysis.next_steps.map(step => ({
          title: change.title.substring(0, 40) + '...',
          action: step.action,
          timeline: step.timeline,
          priority: step.priority || 'medium'
        }))
      ).slice(0, 20); // Limit to 20 items

      if (actionItems.length > 0) {
        const actionTableData = actionItems.map(item => [
          item.title,
          item.action,
          item.timeline,
          item.priority
        ]);

        autoTable(doc, {
          startY: yPos,
          head: [['Document', 'Action', 'Timeline', 'Priority']],
          body: actionTableData,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [59, 130, 246] }
        });
      }
    }

    // Save PDF
    doc.save(`infervia-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Export to Excel
  const exportToExcel = async (data: RegulatoryChange[]) => {
    const workbook = XLSX.utils.book_new();

    // Summary Sheet
    if (selectedSections.summary) {
      const summaryData = [
        ['Infervia Regulatory Changes Report'],
        ['Generated', new Date().toLocaleDateString()],
        [''],
        ['Total Changes', data.length],
        ['Date Range', dateRange],
        [''],
        ['Department Breakdown'],
      ];

      const deptCounts: Record<string, number> = {};
      data.forEach(change => {
        change.affected_departments.forEach(dept => {
          deptCounts[dept] = (deptCounts[dept] || 0) + 1;
        });
      });

      Object.entries(deptCounts).forEach(([dept, count]) => {
        summaryData.push([dept, count]);
      });

      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
    }

    // Regulatory Changes Sheet
    if (selectedSections.updates) {
      const changesData = data.map(change => ({
        'Title': change.title,
        'Source': change.source,
        'Date': change.date || 'N/A',
        'Departments': change.affected_departments.join(', '),
        'Facility Types': change.facility_types.join(', '),
        'Summary': change.simplified_analysis.one_sentence_summary || 
                   change.simplified_analysis.detailed_summary.substring(0, 200),
        'Compliance Deadline': change.simplified_analysis.compliance_deadline || 'N/A',
        'Quality Score': change.quality_control?.quality_score || 'N/A'
      }));

      const changesSheet = XLSX.utils.json_to_sheet(changesData);
      XLSX.utils.book_append_sheet(workbook, changesSheet, 'Regulatory Changes');
    }

    // Action Items Sheet
    if (selectedSections.actionItems) {
      const actionItems = data.flatMap(change => 
        change.simplified_analysis.next_steps.map(step => ({
          'Document': change.title,
          'Action': step.action,
          'Timeline': step.timeline,
          'Owner': step.owner,
          'Priority': step.priority || 'medium'
        }))
      );

      if (actionItems.length > 0) {
        const actionSheet = XLSX.utils.json_to_sheet(actionItems);
        XLSX.utils.book_append_sheet(workbook, actionSheet, 'Action Items');
      }
    }

    // Compliance Sheet
    if (selectedSections.compliance) {
      const complianceData = data
        .filter(change => change.simplified_analysis.compliance_deadline)
        .map(change => ({
          'Title': change.title,
          'Deadline': change.simplified_analysis.compliance_deadline,
          'Key Requirements': change.simplified_analysis.key_requirements?.join('; ') || 'N/A',
          'Departments': change.affected_departments.join(', ')
        }));

      if (complianceData.length > 0) {
        const complianceSheet = XLSX.utils.json_to_sheet(complianceData);
        XLSX.utils.book_append_sheet(workbook, complianceSheet, 'Compliance');
      }
    }

    // Write file
    XLSX.writeFile(workbook, `infervia-report-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Export to Word
  const exportToWord = async (data: RegulatoryChange[]) => {
    const children: any[] = [];

    // Title
    children.push(
      new Paragraph({
        text: 'Infervia Regulatory Changes Report',
        heading: HeadingLevel.HEADING_1,
        spacing: { after: 200 }
      })
    );

    children.push(
      new Paragraph({
        text: `Generated: ${new Date().toLocaleDateString()}`,
        spacing: { after: 400 }
      })
    );

    // Summary Section
    if (selectedSections.summary) {
      children.push(
        new Paragraph({
          text: 'Executive Summary',
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 200 }
        })
      );

      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: `Total Regulatory Changes: `, bold: true }),
            new TextRun({ text: `${data.length}` })
          ],
          spacing: { after: 100 }
        })
      );

      const departments = Array.from(new Set(data.flatMap(d => d.affected_departments)));
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: `Affected Departments: `, bold: true }),
            new TextRun({ text: departments.join(', ') })
          ],
          spacing: { after: 400 }
        })
      );
    }

    // Updates Section
    if (selectedSections.updates) {
      children.push(
        new Paragraph({
          text: 'Regulatory Changes',
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 200 }
        })
      );

      data.slice(0, 10).forEach(change => {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: change.title, bold: true })
            ],
            spacing: { before: 200, after: 100 }
          })
        );

        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: 'Source: ' }),
              new TextRun({ text: change.source })
            ]
          })
        );

        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: 'Summary: ' }),
              new TextRun({ 
                text: change.simplified_analysis.one_sentence_summary || 
                      change.simplified_analysis.detailed_summary.substring(0, 200) 
              })
            ],
            spacing: { after: 200 }
          })
        );
      });
    }

    // Action Items Section
    if (selectedSections.actionItems) {
      children.push(
        new Paragraph({
          text: 'Action Items',
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 }
        })
      );

      const actionItems = data.flatMap(change => 
        change.simplified_analysis.next_steps.slice(0, 3).map(step => ({
          title: change.title,
          action: step.action,
          timeline: step.timeline,
          priority: step.priority
        }))
      ).slice(0, 20);

      actionItems.forEach(item => {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: `• ${item.action}`, bold: true })
            ],
            spacing: { after: 50 }
          })
        );

        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: `  Timeline: ${item.timeline}` })
            ],
            spacing: { after: 100 }
          })
        );
      });
    }

    const doc = new Document({
      sections: [{
        properties: {},
        children: children
      }]
    });

    // Generate and save
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `infervia-report-${new Date().toISOString().split('T')[0]}.docx`);
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);

      console.log('📊 Fetching regulatory changes from API for export...');
      
      // Fetch real data from API
      const allChanges = await apiClient.getRecentChanges(100);
      
      // Filter by date range
      const filteredChanges = filterByDateRange(allChanges);

      console.log(`✅ Exporting ${filteredChanges.length} regulatory changes...`);

      if (filteredChanges.length === 0) {
        alert('No data available for the selected date range.');
        setIsExporting(false);
        return;
      }

      // Export based on selected format
      switch (selectedFormat) {
        case 'pdf':
          await exportToPDF(filteredChanges);
          break;
        case 'excel':
          await exportToExcel(filteredChanges);
          break;
        case 'word':
          await exportToWord(filteredChanges);
          break;
        default:
          throw new Error('Invalid format selected');
      }

      console.log('✅ Export completed successfully!');
      onClose();
    } catch (error) {
      console.error('❌ Export failed:', error);
      alert('Export failed. Please try again or check the console for details.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold">Export Report</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Choose your export format and customize what to include
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" disabled={isExporting}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Format Selection */}
          <div>
            <Label className="text-base font-semibold mb-3 block">Export Format</Label>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => setSelectedFormat('pdf')}
                disabled={isExporting}
                className={`p-4 border-2 rounded-lg transition-all ${
                  selectedFormat === 'pdf'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                } ${isExporting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <FileText className="w-8 h-8 text-red-600 mb-2 mx-auto" />
                <p className="font-medium">PDF</p>
                <p className="text-xs text-muted-foreground">Formatted report</p>
              </button>

              <button
                onClick={() => setSelectedFormat('excel')}
                disabled={isExporting}
                className={`p-4 border-2 rounded-lg transition-all ${
                  selectedFormat === 'excel'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                } ${isExporting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <FileSpreadsheet className="w-8 h-8 text-green-600 mb-2 mx-auto" />
                <p className="font-medium">Excel</p>
                <p className="text-xs text-muted-foreground">Data tables</p>
              </button>

              <button
                onClick={() => setSelectedFormat('word')}
                disabled={isExporting}
                className={`p-4 border-2 rounded-lg transition-all ${
                  selectedFormat === 'word'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                } ${isExporting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <FileImage className="w-8 h-8 text-blue-600 mb-2 mx-auto" />
                <p className="font-medium">Word</p>
                <p className="text-xs text-muted-foreground">Editable document</p>
              </button>
            </div>
          </div>

          {/* Date Range */}
          <div>
            <Label className="text-base font-semibold mb-3 block">Date Range</Label>
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-muted-foreground" />
              <select 
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                disabled={isExporting}
                className="flex-1 px-3 py-2 border border-border rounded-lg"
              >
                <option value="today">Today</option>
                <option value="last-7-days">Last 7 days</option>
                <option value="last-30-days">Last 30 days</option>
                <option value="this-month">This month</option>
                <option value="last-month">Last month</option>
              </select>
            </div>
          </div>

          {/* Sections to Include */}
          <div>
            <Label className="text-base font-semibold mb-3 block">Sections to Include</Label>
            <div className="space-y-2">
              {Object.entries(selectedSections).map(([key, value]) => (
                <label 
                  key={key} 
                  className={`flex items-center space-x-3 p-3 bg-secondary/30 rounded-lg cursor-pointer hover:bg-secondary/50 ${
                    isExporting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => setSelectedSections({...selectedSections, [key]: e.target.checked})}
                    disabled={isExporting}
                    className="w-4 h-4 rounded"
                  />
                  <div className="flex-1">
                    <p className="font-medium capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                  </div>
                  {value && <CheckCircle className="w-5 h-5 text-green-600" />}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <p className="text-sm text-muted-foreground">
            {isExporting ? 'Generating report...' : 'Real data from your API'}
          </p>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={onClose} disabled={isExporting}>
              Cancel
            </Button>
            <Button onClick={handleExport} disabled={isExporting}>
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export Report
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
